import { API_BASE_URL } from '../utils/apiBaseUrl'
import { apiFetch } from '../utils/apiFetch'
import {
  DEFAULT_OPERATION_CONFIG,
  payDateForPeriodEnd,
  payPeriodBounds,
  toLocalYmd,
  upcomingPayPeriods,
  type PayPeriodType,
} from '../utils/controlCenterOperationConfig'

export type PayPeriodConfig = {
  payPeriodType: PayPeriodType
  weekStartsOn: number
  biweeklyAnchorDate: string | null
  payDateWeekday: number
}

let cached: PayPeriodConfig | null = null
let inflight: Promise<PayPeriodConfig> | null = null

export function invalidatePayPeriodCache() {
  cached = null
}

function normalizePayPeriod(raw: unknown): PayPeriodConfig {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
  const payPeriodType: PayPeriodType = ['weekly', 'biweekly'].includes(String(src.payPeriodType))
    ? (String(src.payPeriodType) as PayPeriodType)
    : DEFAULT_OPERATION_CONFIG.payPeriodType
  let weekStartsOn = Number(src.weekStartsOn)
  if (!Number.isInteger(weekStartsOn) || weekStartsOn < 0 || weekStartsOn > 6) {
    weekStartsOn = DEFAULT_OPERATION_CONFIG.weekStartsOn
  }
  let biweeklyAnchorDate: string | null = null
  if (src.biweeklyAnchorDate != null && String(src.biweeklyAnchorDate).trim()) {
    const ymd = String(src.biweeklyAnchorDate).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) biweeklyAnchorDate = ymd
  }
  let payDateWeekday = Number(src.payDateWeekday)
  if (!Number.isInteger(payDateWeekday) || payDateWeekday < 0 || payDateWeekday > 6) {
    payDateWeekday = DEFAULT_OPERATION_CONFIG.payDateWeekday
  }
  return { payPeriodType, weekStartsOn, biweeklyAnchorDate, payDateWeekday }
}

export async function getPayPeriodConfig(): Promise<PayPeriodConfig> {
  if (cached) return cached
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const token = localStorage.getItem('lts_jwt_token')
      const res = await apiFetch(`${API_BASE_URL}/control-center/pay-period`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok || !payload?.success) {
        cached = normalizePayPeriod(DEFAULT_OPERATION_CONFIG)
        return cached
      }
      cached = normalizePayPeriod(payload.data)
      return cached
    } catch {
      cached = normalizePayPeriod(DEFAULT_OPERATION_CONFIG)
      return cached
    } finally {
      inflight = null
    }
  })()
  return inflight
}

export function currentPayPeriod(config: PayPeriodConfig, today = new Date()) {
  const full = { ...DEFAULT_OPERATION_CONFIG, ...config }
  const bounds = payPeriodBounds(full, today)
  const [upcoming] = upcomingPayPeriods(full, today, 1)
  const nextPayDate = upcoming?.payDate
    || payDateForPeriodEnd(bounds.end, config.payDateWeekday)
  return {
    start: toLocalYmd(bounds.start),
    end: toLocalYmd(bounds.end),
    startDate: bounds.start,
    endDate: bounds.end,
    nextPayDate,
  }
}
